import type { Article, ArticleTagSet, ArticleTocEntry } from './types';

/**
 * Minimal article shape the SPA-side helpers below need: tag readout
 * and publish-date picker. Both the detail <see cref="Article"/> and
 * the slim <c>ArticleListItem</c> projection satisfy it structurally.
 */
type ArticleCardish = {
    tags?: ArticleTagSet | null;
    publishedAt?: string | null;
    displayDate?: string | null;
};

/**
 * Article helpers extracted from the legacy Articles SPA. These wrap the
 * raw <see cref="Article"/> wire shape with the small adapters every page
 * needs: cover URL derivation, TOC JSON parsing, tag readout, date
 * formatting, deep-link builder.
 */

/**
 * Named MediaRecipes the SPA renders article covers through. Each name
 * matches a [MediaRecipe] declared server-side in
 * Gposingway.Authoring.Application.AuthoringMediaRecipes. Every recipe
 * pins its dimensions / format / quality - the SPA picks the right
 * recipe for the slot instead of overriding at request time. Locking the
 * public render path to the recipe set means MEDIA-0007 can pre-warm
 * every visible variant and no third-party caller can force a cold
 * render with arbitrary w/h.
 *
 * <para>Surface to dimensions map:
 * <list type="bullet">
 *   <item><c>article-card</c>          - 480x480 (Articles index grid)</item>
 *   <item><c>article-card-sidebar</c>  - 400x400 (LatestArticle sidebar slot)</item>
 *   <item><c>article-hero</c>          - 2000x800 (article detail header)</item>
 *   <item><c>article-hero-landing</c>  - 900x675 (LatestArticle landing hero)</item>
 *   <item><c>admin-preview</c>         - 480px wide (article-edit preview)</item>
 * </list></para>
 */
export type ArticleCoverRecipe =
    | 'article-card'
    | 'article-card-sidebar'
    | 'article-hero'
    | 'article-hero-landing'
    | 'admin-preview';

/**
 * Build the URL the SPA loads a cover image from. Resolves to
 * <c>/media/{coverMediaId}/{recipe}</c> per MEDIA-0004's recipe pipeline.
 * Returns null when the article has no cover - callers render the
 * placeholder gradient instead.
 *
 * Signature accepts any shape carrying a <c>coverMediaId</c> field so
 * the helper works for full <see cref="Article"/> rows and lighter
 * landing-feed shapes (FeaturedArticle, etc.).
 */
export function coverUrl(
    article: { coverMediaId?: string | null },
    recipe: ArticleCoverRecipe,
): string | null {
    if (!article.coverMediaId) return null;
    return `/media/${encodeURIComponent(article.coverMediaId)}/${recipe}`;
}

/**
 * Parse the article's <c>renderedToc</c> JSON into TOC entries the
 * sidebar can render. Returns an empty array when the field is null,
 * empty, or invalid JSON - never throws on read.
 *
 * <para>The wire shape uses lowercase keys (matching the camelCase
 * default policy the Article renderer emits); the legacy SPA used
 * PascalCase. Tolerate both so a future renderer refactor doesn't
 * silently break the sidebar.</para>
 */
export function parseToc(article: Article): ArticleTocEntry[] {
    const raw = article.renderedToc;
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry) => normaliseTocEntry(entry))
            .filter((entry): entry is ArticleTocEntry => entry !== null);
    } catch {
        return [];
    }
}

function normaliseTocEntry(entry: unknown): ArticleTocEntry | null {
    if (typeof entry !== 'object' || entry === null) return null;
    const rec = entry as Record<string, unknown>;
    const level = rec.level ?? rec.Level;
    const anchor = rec.anchor ?? rec.Anchor;
    const text = rec.text ?? rec.Text;
    if (typeof text !== 'string' || text.length === 0) return null;
    if (level !== 2 && level !== 3) return null;
    return {
        level,
        anchor: typeof anchor === 'string' ? anchor : null,
        text,
    };
}

/**
 * Read the article's public-scope tags (axis flatten). Prefers the
 * already-flattened <c>publicTags</c>; falls back to <c>public.flat</c>
 * when the controller projects the long shape. Returns an empty array
 * when the entity has no Tags set.
 */
export function publicTags(article: ArticleCardish): string[] {
    return article.tags?.publicTags ?? article.tags?.public?.flat ?? [];
}

/**
 * Pick the article's effective publish-date ISO string for display.
 * Prefers <c>displayDate</c> (back-dated import override) over
 * <c>publishedAt</c>. Returns null when neither is set. Callers pair
 * this with <c>useDateFormat()</c> from i18n so the rendered date
 * tracks the active locale instead of hardcoding the browser default.
 */
export function pickPublishDate(article: ArticleCardish): string | null {
    return article.displayDate ?? article.publishedAt ?? null;
}

/**
 * Build the deep-link to a single article. Id-routed with a decorative
 * trailing slug (`/articles/{id}/{slug}`, E3): the id is the resolution key,
 * the slug is cosmetic and omitted when absent.
 */
export function articleHref(rootHref: string, id: string, slug?: string | null): string {
    const base = rootHref.endsWith('/') ? rootHref.slice(0, -1) : rootHref;
    const tail = slug && slug.length > 0 ? `/${encodeURIComponent(slug)}` : '';
    return `${base}/articles/${encodeURIComponent(id)}${tail}`;
}
